import {Dispatch} from 'redux'
import {getCurrentBookingRequestId} from 'utils/bookingRequestId'

import {getTrackTimingEventPayload} from '@vio/core/src/_web/utils/analytics/trackPerformanceMetrics'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Entity, Page, Team} from '@vio/core/src/analytics/types/Events'

/**
 * A booking either reaches the confirmation page on the trip that starts at the
 * Complete Booking click, or hands off to a 3DS challenge or an APM redirect
 * and gets there on a second trip. The APM handoff is a full page navigation,
 * so the two trips are timed separately, and `Total` is their sum: the time
 * the booking spent with us, whichever route it took.
 */
export enum BookingLatencyMetric {
  Submission = 'BookingSubmission',
  Resumption = 'BookingResumption',
  Total = 'BookingTotal'
}

/**
 * How a timed trip ended. `Confirmed` means the user is looking at their
 * booking, not merely that the status poll returned one. Both handoffs can only
 * end a submission trip.
 */
export enum BookingLatencyOutcome {
  Confirmed = 'confirmed',
  ThreeDomainSecure = 'three_domain_secure',
  PaymentRedirect = 'payment_redirect',
  Errored = 'errored'
}

export type BookingHandoff =
  | BookingLatencyOutcome.ThreeDomainSecure
  | BookingLatencyOutcome.PaymentRedirect

/** Navigation start on the `performance.now` timeline. */
export const NAVIGATION_START = 0

const BOOKING_ATTEMPT_KEY = 'bookingLatencyAttempt'

interface BookingAttempt {
  /**
   * The trips measured so far, summed. Kept as a running total rather than a
   * start timestamp so the time the user spends answering a challenge or
   * paying at a provider, which is theirs and not ours, stays out of it. It
   * also keeps every measure on a per-document monotonic clock, which a
   * timestamp could not do across the APM redirect.
   */
  elapsedMilliseconds: number
  handoff?: BookingHandoff
  handoffBookingId?: string
  isResumptionTimed?: boolean
  hasSubmission?: boolean
  isComplete?: boolean
}

let storageAvailable = true

const readBookingAttempt = (): BookingAttempt | null => {
  if (!storageAvailable) return null

  try {
    const stored = window.sessionStorage.getItem(BOOKING_ATTEMPT_KEY)
    if (!stored) return null
    return JSON.parse(stored) as BookingAttempt
  } catch {
    storageAvailable = false
    return null
  }
}

const writeBookingAttempt = (attempt: BookingAttempt): void => {
  if (!storageAvailable) return
  try {
    window.sessionStorage.setItem(BOOKING_ATTEMPT_KEY, JSON.stringify(attempt))
  } catch {
    // Discard totals after a storage failure; saved trips may be incomplete.
    storageAvailable = false
  }
}

/** Opens the whole-booking measure, discarding any abandoned earlier attempt. */
export const startBookingAttempt = (): void =>
  writeBookingAttempt({elapsedMilliseconds: 0})

const clearBookingAttempt = (): void => {
  if (!storageAvailable) return
  try {
    window.sessionStorage.removeItem(BOOKING_ATTEMPT_KEY)
  } catch {
    storageAvailable = false
  }
}

const addTripToBookingAttempt = (
  duration: number,
  metric: BookingLatencyMetric,
  outcome: BookingLatencyOutcome
): void => {
  const attempt = readBookingAttempt()
  if (!attempt) return

  writeBookingAttempt({
    ...attempt,
    elapsedMilliseconds: attempt.elapsedMilliseconds + duration,
    hasSubmission:
      attempt.hasSubmission || metric === BookingLatencyMetric.Submission,
    isComplete:
      outcome === BookingLatencyOutcome.Confirmed &&
      (attempt.handoff
        ? metric === BookingLatencyMetric.Resumption
        : metric === BookingLatencyMetric.Submission)
  })
}

/**
 * Records that the booking left for a challenge or a payment provider. The
 * booking id is what lets the page the redirect lands on tell a resumed
 * booking apart from the manual status recovery, which shares that URL.
 */
export const markBookingHandoff = (
  handoff: BookingHandoff,
  handoffBookingId?: string
): void => {
  const attempt = readBookingAttempt()
  if (!attempt) return

  writeBookingAttempt({...attempt, handoff, handoffBookingId})
}

/**
 * Whether this page is the return leg of the redirect that left the mark.
 * Answers true once, so a re-render does not restart the resumption measure.
 * @param bookingId - The booking the current page is resuming.
 */
export const takeRedirectedBookingResumption = (
  bookingId?: string
): boolean => {
  const attempt = readBookingAttempt()
  const isResuming =
    attempt?.handoff === BookingLatencyOutcome.PaymentRedirect &&
    Boolean(bookingId) &&
    attempt.handoffBookingId === bookingId &&
    !attempt.isResumptionTimed

  if (isResuming && attempt) {
    writeBookingAttempt({...attempt, isResumptionTimed: true})
  }

  return isResuming
}

interface BookingTrip {
  metric: BookingLatencyMetric
  startedAt: number
  resumedVia?: BookingHandoff
}

/**
 * Only one trip is ever in flight, because a submission ends before the
 * resumption that follows it begins. Keeping it here, rather than deriving it
 * from the booking stage, is what stops a page that was never handed off from
 * reporting a resumption it did not make.
 */
let activeTrip: BookingTrip | null = null

interface StartBookingLatencyArg {
  metric: BookingLatencyMetric
  /** The handoff being resumed from, recorded by the side that knows it. */
  resumedVia?: BookingHandoff
  /**
   * When the trip began, on the page's `performance.now` timeline. Defaults to
   * now; a trip that opens with a page load passes `NAVIGATION_START` so the
   * measure covers the load the user waited through.
   */
  startedAt?: number
}

export const startBookingLatency = ({
  metric,
  resumedVia,
  startedAt = performance.now()
}: StartBookingLatencyArg): void => {
  activeTrip = {metric, resumedVia, startedAt}
}

interface DispatchMeasureArg {
  dispatch: Dispatch
  metric: BookingLatencyMetric
  duration: number
  payload: {
    outcome: BookingLatencyOutcome
    resumedVia?: BookingHandoff
    /**
     * Whether the booking took two trips. Not to be confused with a split
     * booking, one stay across two offers, which every checkout event already
     * carries as `offerContext.isSplitBooking`.
     */
    wasHandedOff?: boolean
    paymentMethod?: string
    bookingId?: string
  }
}

const dispatchMeasure = ({
  dispatch,
  metric,
  duration,
  payload
}: DispatchMeasureArg): void => {
  dispatch(
    trackEvent({
      ...getTrackTimingEventPayload({
        metric,
        entity: Entity.Booking,
        team: Team.Default,
        duration,
        page: Page.Payment
      }),
      payload: {
        ...payload,
        // Stated rather than left to be inferred from the outcome vocabulary,
        // so "did this booking go through" is one filter on every event.
        isBookingComplete: payload.outcome === BookingLatencyOutcome.Confirmed,
        bookingRequestId: getCurrentBookingRequestId()
      }
    })
  )
}

interface TrackBookingLatencyArg {
  dispatch: Dispatch
  outcome: BookingLatencyOutcome
  bookingId?: string
  paymentMethod?: string
}

/**
 * Emits the elapsed time of the trip in flight and ends it, so each trip
 * reports once and an untimed one reports nothing.
 */
export const trackBookingLatency = ({
  dispatch,
  outcome,
  bookingId,
  paymentMethod
}: TrackBookingLatencyArg): void => {
  if (!activeTrip) return

  const {metric, resumedVia, startedAt} = activeTrip
  activeTrip = null

  const duration = Math.round(performance.now() - startedAt)

  if (outcome === BookingLatencyOutcome.Errored) {
    // A booking that failed has no total to report, and leaving its attempt
    // open would let the manual status recovery, which lands on the
    // confirmation page too, close it as though the booking had gone through.
    clearBookingAttempt()
  } else {
    addTripToBookingAttempt(duration, metric, outcome)
  }
  dispatchMeasure({
    dispatch,
    metric,
    duration,
    payload: {outcome, resumedVia, paymentMethod, bookingId}
  })
}

interface TrackBookingTotalArg {
  dispatch: Dispatch
  bookingId?: string
  paymentMethod?: string
}

/**
 * Emits the booking's trips as one measure and closes the attempt. This is the
 * time the booking spent with us: it excludes the wait at a 3DS challenge or a
 * payment provider, so it is shorter than the wall clock for a handed-off
 * booking. Only a booking that reached the confirmation page has a total, so
 * this event existing is what says it completed.
 */
export const trackBookingTotal = ({
  dispatch,
  bookingId,
  paymentMethod
}: TrackBookingTotalArg): void => {
  const attempt = readBookingAttempt()
  if (!attempt) return

  clearBookingAttempt()

  // Reloads lose the open trip, so a persisted partial sum is not a total.
  if (!attempt.hasSubmission || !attempt.isComplete) return

  dispatchMeasure({
    dispatch,
    metric: BookingLatencyMetric.Total,
    duration: attempt.elapsedMilliseconds,
    payload: {
      outcome: BookingLatencyOutcome.Confirmed,
      resumedVia: attempt.handoff,
      wasHandedOff: Boolean(attempt.handoff),
      paymentMethod,
      bookingId
    }
  })
}
